import { PlotJsonError } from "./plotjson-error.js";
/**
 * Finite guardrails for untrusted PlotJSON input.
 *
 * They are safety defaults rather than product-size or latency guarantees.
 * Applications may provide smaller or larger finite positive safe integers.
 */
export const DEFAULT_PLOTJSON_LIMITS = Object.freeze({
    inputBytes: 16 * 1024 * 1024,
    depth: 128,
    totalNodes: 1_000_000,
    objectKeys: 250_000,
    stringLength: 1_000_000,
    features: 100_000,
    controlPointsPerFeature: 10_000,
    totalControlPoints: 1_000_000,
});
const LIMIT_NAMES = Object.freeze([
    "inputBytes",
    "depth",
    "totalNodes",
    "objectKeys",
    "stringLength",
    "features",
    "controlPointsPerFeature",
    "totalControlPoints",
]);
/** Validates and freezes a complete limit set. */
export function resolvePlotJsonLimits(overrides = {}) {
    const resolved = {};
    for (const name of LIMIT_NAMES) {
        const value = overrides[name] ?? DEFAULT_PLOTJSON_LIMITS[name];
        if (!Number.isSafeInteger(value) || value <= 0) {
            throw new PlotJsonError("PLOTJSON_RESOURCE_LIMIT_EXCEEDED", `PlotJSON limit "${name}" must be a finite positive safe integer.`, { limitName: name, actual: value });
        }
        resolved[name] = value;
    }
    return Object.freeze(resolved);
}
/** Returns UTF-8 byte size and rejects a string beyond the configured limit. */
export function assertPlotJsonInputSize(input, limits = {}) {
    const resolved = resolvePlotJsonLimits(limits);
    const bytes = new TextEncoder().encode(input).byteLength;
    if (bytes > resolved.inputBytes) {
        throw limitExceeded("inputBytes", resolved.inputBytes, bytes, "$");
    }
    return bytes;
}
/**
 * Validates and deep-clones untrusted JSON-like data without invoking accessors.
 *
 * Traversal is iterative, object keys are visited lexicographically, cycles are
 * rejected, and repeated non-cyclic references are cloned independently.
 */
export function clonePlotJsonValue(input, options = {}) {
    const limits = resolvePlotJsonLimits(options.limits);
    const rootPath = options.path ?? "$";
    if (rootPath.length === 0) {
        throw new PlotJsonError("PLOTJSON_CURRENT_SCHEMA_INVALID", "PlotJSON root path must not be empty.");
    }
    const root = {};
    const statistics = {
        totalNodes: 0,
        objectKeys: 0,
        maximumDepth: 0,
        maximumStringLength: 0,
        features: 0,
        maximumControlPointsPerFeature: 0,
        totalControlPoints: 0,
    };
    const activeAncestors = new Set();
    const stack = [{
            kind: "visit",
            source: input,
            assignment: { kind: "root", target: root },
            path: rootPath,
            depth: 0,
            role: "root",
        }];
    while (stack.length > 0) {
        const frame = stack.pop();
        if (frame.kind === "exit") {
            activeAncestors.delete(frame.source);
            continue;
        }
        registerNode(statistics, limits, frame.path, frame.depth);
        const source = frame.source;
        if (source === null) {
            assign(frame.assignment, null);
            continue;
        }
        switch (typeof source) {
            case "string":
                registerString(statistics, limits, source, frame.path);
                assign(frame.assignment, source);
                continue;
            case "boolean":
                assign(frame.assignment, source);
                continue;
            case "number":
                if (!Number.isFinite(source)) {
                    throw nonJson(frame.path, "numbers must be finite");
                }
                assign(frame.assignment, source);
                continue;
            case "undefined":
            case "bigint":
            case "symbol":
            case "function":
                throw nonJson(frame.path, `${typeof source} values are not JSON`);
            case "object":
                break;
            default:
                throw nonJson(frame.path, "value is not JSON");
        }
        const object = source;
        if (activeAncestors.has(object)) {
            throw nonJson(frame.path, "cyclic references are not JSON");
        }
        activeAncestors.add(object);
        if (Array.isArray(object)) {
            const length = object.length;
            ensureArrayCapacity(statistics, limits, length, frame.path);
            registerSemanticArray(statistics, limits, frame.role, length, frame.path);
            const target = new Array(length);
            assign(frame.assignment, target);
            stack.push({ kind: "exit", source: object });
            const descriptors = inspectArray(object, frame.path, statistics, limits);
            for (let index = length - 1; index >= 0; index -= 1) {
                const descriptor = descriptors[index];
                stack.push({
                    kind: "visit",
                    source: descriptor.value,
                    assignment: { kind: "array", target, index },
                    path: appendIndex(frame.path, index),
                    depth: frame.depth + 1,
                    role: frame.role === "features-array" ? "feature-entry" : "other",
                });
            }
            continue;
        }
        ensurePlainObject(object, frame.path);
        const entries = inspectObject(object, frame.path, statistics, limits);
        statistics.objectKeys += entries.length;
        if (statistics.objectKeys > limits.objectKeys) {
            throw limitExceeded("objectKeys", limits.objectKeys, statistics.objectKeys, frame.path);
        }
        const target = {};
        assign(frame.assignment, target);
        stack.push({ kind: "exit", source: object });
        for (let index = entries.length - 1; index >= 0; index -= 1) {
            const [key, descriptor] = entries[index];
            stack.push({
                kind: "visit",
                source: descriptor.value,
                assignment: { kind: "object", target, key },
                path: appendKey(frame.path, key),
                depth: frame.depth + 1,
                role: roleForObjectChild(frame.role, key),
            });
        }
    }
    if (!("value" in root)) {
        throw new PlotJsonError("PLOTJSON_VALUE_NOT_JSON", "PlotJSON clone did not produce a root value.", { path: rootPath });
    }
    return Object.freeze({
        value: root.value,
        statistics: freezeStatistics(statistics),
        limits,
    });
}
export function scanPlotJsonValue(input, options = {}) {
    return clonePlotJsonValue(input, options).statistics;
}
function registerNode(statistics, limits, path, depth) {
    if (depth > limits.depth) {
        throw limitExceeded("depth", limits.depth, depth, path);
    }
    statistics.maximumDepth = Math.max(statistics.maximumDepth, depth);
    statistics.totalNodes += 1;
    if (statistics.totalNodes > limits.totalNodes) {
        throw limitExceeded("totalNodes", limits.totalNodes, statistics.totalNodes, path);
    }
}
function registerString(statistics, limits, value, path) {
    statistics.maximumStringLength = Math.max(statistics.maximumStringLength, value.length);
    if (value.length > limits.stringLength) {
        throw limitExceeded("stringLength", limits.stringLength, value.length, path);
    }
}
function ensureArrayCapacity(statistics, limits, length, path) {
    const remaining = limits.totalNodes - statistics.totalNodes;
    if (length > remaining) {
        throw limitExceeded("totalNodes", limits.totalNodes, statistics.totalNodes + length, path);
    }
}
function registerSemanticArray(statistics, limits, role, length, path) {
    if (role === "features-array") {
        statistics.features = length;
        if (length > limits.features) {
            throw limitExceeded("features", limits.features, length, path);
        }
    }
    if (role === "control-points-array") {
        statistics.maximumControlPointsPerFeature = Math.max(statistics.maximumControlPointsPerFeature, length);
        if (length > limits.controlPointsPerFeature) {
            throw limitExceeded("controlPointsPerFeature", limits.controlPointsPerFeature, length, path);
        }
        statistics.totalControlPoints += length;
        if (statistics.totalControlPoints > limits.totalControlPoints) {
            throw limitExceeded("totalControlPoints", limits.totalControlPoints, statistics.totalControlPoints, path);
        }
    }
}
function inspectArray(value, path, statistics, limits) {
    let keys;
    try {
        keys = Reflect.ownKeys(value);
    }
    catch (cause) {
        throw nonJson(path, "array keys could not be inspected", cause);
    }
    for (const key of keys) {
        if (typeof key === "symbol") {
            throw nonJson(path, "symbol array keys are not JSON");
        }
        if (key === "length")
            continue;
        const index = canonicalArrayIndex(key);
        if (index === undefined || index >= value.length) {
            registerString(statistics, limits, key, path);
            throw nonJson(appendKey(path, key), "custom array properties are not JSON");
        }
    }
    const descriptors = [];
    for (let index = 0; index < value.length; index += 1) {
        const key = String(index);
        let descriptor;
        try {
            descriptor = Object.getOwnPropertyDescriptor(value, key);
        }
        catch (cause) {
            throw nonJson(appendIndex(path, index), "array item could not be inspected", cause);
        }
        if (!descriptor) {
            throw nonJson(appendIndex(path, index), "sparse arrays are not JSON");
        }
        validateDataDescriptor(descriptor, appendIndex(path, index));
        descriptors.push(descriptor);
    }
    return descriptors;
}
function ensurePlainObject(value, path) {
    let prototype;
    try {
        prototype = Object.getPrototypeOf(value);
    }
    catch (cause) {
        throw nonJson(path, "object prototype could not be inspected", cause);
    }
    if (prototype !== Object.prototype && prototype !== null) {
        throw nonJson(path, "only plain objects are valid JSON objects");
    }
}
function inspectObject(value, path, statistics, limits) {
    let keys;
    try {
        keys = Reflect.ownKeys(value);
    }
    catch (cause) {
        throw nonJson(path, "object keys could not be inspected", cause);
    }
    for (const key of keys) {
        if (typeof key === "symbol") {
            throw nonJson(path, "symbol object keys are not JSON");
        }
    }
    const names = keys.slice().sort(compareStrings);
    return names.map((key) => {
        registerString(statistics, limits, key, path);
        let descriptor;
        try {
            descriptor = Object.getOwnPropertyDescriptor(value, key);
        }
        catch (cause) {
            throw nonJson(appendKey(path, key), "object property could not be inspected", cause);
        }
        if (!descriptor) {
            throw nonJson(appendKey(path, key), "object property disappeared during inspection");
        }
        validateDataDescriptor(descriptor, appendKey(path, key));
        return Object.freeze([key, descriptor]);
    });
}
function validateDataDescriptor(descriptor, path) {
    if (!("value" in descriptor)) {
        throw nonJson(path, "accessor properties are not JSON");
    }
    if (!descriptor.enumerable) {
        throw nonJson(path, "non-enumerable properties are not JSON");
    }
}
function roleForObjectChild(parentRole, key) {
    if (parentRole === "root" && key === "features")
        return "features-array";
    if (parentRole === "feature-entry" && key === "controlPoints") {
        return "control-points-array";
    }
    return "other";
}
function assign(assignment, value) {
    if (assignment.kind === "root") {
        assignment.target.value = value;
        return;
    }
    if (assignment.kind === "array") {
        assignment.target[assignment.index] = value;
        return;
    }
    Object.defineProperty(assignment.target, assignment.key, {
        value,
        enumerable: true,
        configurable: true,
        writable: true,
    });
}
function canonicalArrayIndex(key) {
    if (!/^(0|[1-9]\d*)$/.test(key))
        return undefined;
    const index = Number(key);
    return Number.isSafeInteger(index) && String(index) === key ? index : undefined;
}
function appendIndex(path, index) {
    return `${path}[${index}]`;
}
function appendKey(path, key) {
    return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key)
        ? `${path}.${key}`
        : `${path}[${JSON.stringify(key)}]`;
}
function compareStrings(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
function nonJson(path, reason, cause) {
    return new PlotJsonError("PLOTJSON_VALUE_NOT_JSON", `PlotJSON value at ${path} is invalid: ${reason}.`, cause === undefined ? { path } : { path, cause });
}
function limitExceeded(name, limit, actual, path) {
    return new PlotJsonError("PLOTJSON_RESOURCE_LIMIT_EXCEEDED", `PlotJSON limit "${name}" was exceeded at ${path}: ${actual} > ${limit}.`, { path, limitName: name, limit, actual });
}
function freezeStatistics(statistics) {
    return Object.freeze({
        totalNodes: statistics.totalNodes,
        objectKeys: statistics.objectKeys,
        maximumDepth: statistics.maximumDepth,
        maximumStringLength: statistics.maximumStringLength,
        features: statistics.features,
        maximumControlPointsPerFeature: statistics.maximumControlPointsPerFeature,
        totalControlPoints: statistics.totalControlPoints,
    });
}
//# sourceMappingURL=plotjson-safety.js.map